import axios from "axios";
import {
  BASE_URL,
  PRODUCT_URL,
  CATEGORY_URL,
  EMAIL_URL,
  LATEST_PRODUCT_URL,
} from "../config/Url";

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000, // Timeout in milliseconds
  headers: {
    "Content-Type": "application/json",
  },
});

// Example fetchProductData function
export const fetchProductData = async ({
  page = 1,
  pageSize = 12,
  name = "",
  sortBy = "brandName",
  sortOrder = "ASC",
}) => {
  // If search term is provided, use the search endpoint
  const endpoint = name ? `${PRODUCT_URL}/search` : PRODUCT_URL;

  // Build query parameters
  const params = new URLSearchParams();
  params.append("page", page);
  params.append("pageSize", pageSize);

  // Only add name parameter if it's not empty
  if (name) {
    params.append("name", name);
  }

  // Add sorting params
  params.append("sortBy", sortBy);
  params.append("sortOrder", sortOrder);

  const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);

  if (response.status !== 200) {
    throw new Error("Network response was not ok");
  }

  const data = response.data;
  return {
    products: data.data,
    totalPages: data.totalPages,
    currentPage: data.currentPage,
    totalItems: data.totalItems,
    total: data.total,
  };
};

// Fetch all products across all pages for search functionality
export const fetchAllProductsForSearch = async () => {
  try {
    // First get the total count of products
    const initialResponse = await axiosInstance.get(
      `${PRODUCT_URL}?page=1&pageSize=1`
    );
    if (initialResponse.status !== 200) {
      throw new Error("Network response was not ok");
    }

    const totalItems = initialResponse.data.total; // Updated to use 'total' from API response
    // Fetch all products in one request with a large enough page size
    const response = await axiosInstance.get(
      `${PRODUCT_URL}?page=1&pageSize=${totalItems}`
    );
    if (response.status !== 200) {
      throw new Error("Network response was not ok");
    }

    return {
      products: response.data.data || [],
      totalItems: response.data.total, // Updated to use 'total' from API response
    };
  } catch (error) {
    console.error("Error fetching all products:", error);
    throw error;
  }
};

export const fetchProductById = async ({ queryKey }) => {
  const [_key, { id }] = queryKey;
  const response = await axiosInstance.get(PRODUCT_URL + `/${id}`);
  if (response.status !== 200) {
    throw new Error("Network response was not ok");
  }
  return await response.data.data;
};

export const sendContactMessage = async (formData) => {
  // Add an artificial delay to make the loading state more visible
  await new Promise((resolve) => setTimeout(resolve, 1000));

  const response = await axiosInstance.post(EMAIL_URL, formData);
  if (response.status !== 200) {
    throw new Error("Failed to send message");
  }
  return response.data;
};

export const fetchCategories = async () => {
  const response = await axiosInstance.get(`${CATEGORY_URL}`);
  if (response.status !== 200) {
    throw new Error("Network response was not ok");
  }
  return response.data.data || []; // Return the data array or empty array if not available
};

export const fetchLatestProducts = async () => {
  const response = await axiosInstance.get(`${LATEST_PRODUCT_URL}`);
  if (response.status !== 200) {
    throw new Error("Network response was not ok");
  }
  return response.data.data || []; // Return the data array or empty array if not available
};

export const fetchProductsByCategory = async ({
  category,
  page = 1,
  pageSize = 12,
}) => {
  if (!category) {
    throw new Error("Category parameter is required");
  }

  try {
    // console.log(`Fetching products for category: ${category}, page: ${page}, pageSize: ${pageSize}`);

    const response = await axiosInstance.get(
      `${PRODUCT_URL}/category/${category}?page=${page}&pageSize=${pageSize}`
    );

    if (response.status !== 200) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    // console.log(`Received ${response.data?.data?.length || 0} products for category ${category}`);

    return {
      products: response.data.data || [],
      totalPages: response.data.totalPages || 0,
      currentPage: response.data.currentPage || 1,
      totalResults: response.data.total || 0, // Updated to use 'total' from API response
    };
  } catch (error) {
    console.error(`Error fetching products for category ${category}:`, error);
    throw error;
  }
};
// Add more API functions as needed
